import {match} from 'ts-pattern';

import {mergeAll} from 'ramda';
import {isNilOrEmpty, isString} from 'ramda-adjunct';

import {workshopRoutes} from '@dms/routes';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  logError,
  queryParams,
  workshopSearchParams,
} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {SERVICE_ORDER_PDF_COLUMNS} from '../../../../constants/SERVICE_ORDER_PDF_COLUMNS';
import {useServiceOrderPdfExport} from '../../../../hooks/useServiceOrderPdfExport';

const PREDEFINED_COLUMNS = ['serviceCaseId'] as const;

export function ServiceOrderOverview() {
  const navigate = useNavigate();
  const onPdfExportCallback = useServiceOrderPdfExport();

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
  }) => {
    if (!isString(rowId)) {
      return logError(`Bulk invocation of ${actionKey} is not implemented`);
    }
    const serviceCaseId = getColumnData<string>('serviceCaseId')?.value;
    if (isNilOrEmpty(serviceCaseId)) {
      return logError('Missing service case ID');
    }
    const path = composePath(workshopRoutes.serviceCaseDetail, {
      params: {
        id: serviceCaseId,
      },
      queryParams: {
        source: 'service-orders',
        section: workshopSearchParams.serviceDetail.orders,
        [queryParams.SERVICE_CASE_ORDER_ID]: rowId,
      },
    });

    match(actionKey)
      .with('redirectDetail', () => navigate(path))
      .with('redirectDetail_newTab', () => window.open(path))
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const queryModifier = (filter: QueryFilterObject) => mergeAll([filter, {vehicleId: undefined}]);

  return (
    <DmsDataGrid
      data-testid="service-order-overview"
      gridCode="service-order"
      actionCallback={actionCallback}
      predefinedColumns={PREDEFINED_COLUMNS}
      pdfColumns={SERVICE_ORDER_PDF_COLUMNS}
      onPdfExportCallback={onPdfExportCallback}
      queryModifier={queryModifier}
    />
  );
}
